import React, { useState, useRef, useEffect } from "react"
import { Card, Flex, Button, Typography, Tag, Select } from "antd"
import { LeftOutlined, RightOutlined, BookOutlined, FileTextOutlined } from "@ant-design/icons"
import styles from "./EquipmentCalendar.module.css"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"

const { Title, Text } = Typography

const EquipmentCalendar = ({ calendarData, loading }) => {
  const { t } = useTranslation()
  const [activeFilter, setActiveFilter] = useState("with_bookings")
  const [viewMode, setViewMode] = useState("week")
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [selectedAsset, setSelectedAsset] = useState("all")
  const dayGridRef = useRef(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)

  // Get week dates (Sunday to Saturday)
  const getWeekDates = () => {
    const current = new Date(selectedDate)
    const sunday = new Date(current)
    sunday.setDate(current.getDate() - current.getDay())

    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(sunday)
      date.setDate(sunday.getDate() + i)
      return date
    })
  }

  // Get month calendar grid (6 weeks x 7 days)
  const getMonthDates = () => {
    const year = selectedDate.getFullYear()
    const month = selectedDate.getMonth()

    // First day of the month
    const firstDay = new Date(year, month, 1)

    // Start from the Sunday before or on the first day
    const startDate = new Date(firstDay)
    startDate.setDate(firstDay.getDate() - firstDay.getDay())

    // Generate 6 weeks (42 days)
    const dates = []
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + i)
      dates.push({
        date,
        isCurrentMonth: date.getMonth() === month,
        dayNumber: date.getDate()
      })
    }

    return dates
  }

  // Format date display
  const formatDateDisplay = () => {
    if (viewMode === "week") {
      const weekDates = getWeekDates()
      const startDate = weekDates[0]
      const endDate = weekDates[6]

      const options = { month: "long", day: "numeric" }
      const year = endDate.getFullYear()

      return `${startDate.toLocaleDateString("en-US", options)}-${endDate.getDate()}, ${year}`
    }

    if (viewMode === "month") {
      const options = { month: "long", year: "numeric" }
      return selectedDate.toLocaleDateString("en-US", options)
    }

    const options = { month: "long", day: "numeric", year: "numeric" }
    return selectedDate.toLocaleDateString("en-US", options)
  }

  // Navigate to previous/next day
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate)
    if (viewMode === "day") {
      newDate.setDate(newDate.getDate() + direction)
    } else if (viewMode === "week") {
      newDate.setDate(newDate.getDate() + direction * 7)
    } else {
      newDate.setMonth(newDate.getMonth() + direction)
    }
    setSelectedDate(newDate)
  }

  // Go to today
  const goToToday = () => {
    setSelectedDate(new Date())
  }

  // Format journal date (e.g., "Saturday, August 19 Journal")
  const formatJournalDate = () => {
    const options = { weekday: "long", month: "long", day: "numeric" }
    return `${selectedDate.toLocaleDateString("en-US", options)} Journal`
  }

  // Handle horizontal scroll for day view assets
  const handleScroll = (direction) => {
    if (dayGridRef.current) {
      const scrollAmount = 300 // Scroll by 300px
      const currentScroll = dayGridRef.current.scrollLeft
      const newScroll = direction === "left"
        ? currentScroll - scrollAmount
        : currentScroll + scrollAmount

      dayGridRef.current.scrollTo({
        left: newScroll,
        behavior: "smooth"
      })

      // Sync scroll position to all asset column rows
      syncScrollPosition(newScroll)

      // Update arrow visibility after scroll
      setTimeout(() => checkScrollPosition(), 300)
    }
  }

  // Sync scroll position between header and body rows
  const syncScrollPosition = (scrollLeft) => {
    // Get all asset column containers in the body
    const assetColumnScrolls = document.querySelectorAll(`.${styles.assetColumnsScroll}`)
    assetColumnScrolls.forEach(scroll => {
      scroll.scrollLeft = scrollLeft
    })
  }

  // Check scroll position and update arrow visibility
  const checkScrollPosition = () => {
    if (dayGridRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = dayGridRef.current
      setShowLeftArrow(scrollLeft > 0)
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10)
    }
  }

  // Handle header scroll and sync with body
  const handleHeaderScroll = (e) => {
    const scrollLeft = e.target.scrollLeft
    syncScrollPosition(scrollLeft)
    checkScrollPosition()
  }

  // Check scroll position on mount and when view mode changes
  useEffect(() => {
    if (viewMode === "day") {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => checkScrollPosition(), 100)
    }
  }, [viewMode, calendarData])

  // Filter tabs
  const filterTabs = [
    { key: "all", label: t("equipment.calendar.all_assets") },
    { key: "with_bookings", label: t("equipment.calendar.assets_with_bookings") },
    { key: "without_bookings", label: t("equipment.calendar.assets_without_bookings") }
  ]

  // View mode buttons
  const viewModes = [
    { key: "day", label: t("equipment.calendar.day") },
    { key: "week", label: t("equipment.calendar.week") },
    { key: "month", label: t("equipment.calendar.month") }
  ]

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode)
    // Reset asset filter when switching to month view
    if (mode === "month") {
      setSelectedAsset("all")
    }
  }

  // Generate time slots (5 AM to 10 AM for now, can be adjusted)
  const timeSlots = Array.from({ length: 18 }, (_, i) => {
    const hour = i + 5
    const ampm = hour >= 12 ? "PM" : "AM"
    const displayHour = hour > 12 ? hour - 12 : hour
    return `${displayHour} ${ampm}`
  })

  // Mock assets data - will be replaced with real data
  const getMockBookings = () => {
    const weekDates = getWeekDates()
    return [
      {
        id: 1,
        number: "#0001",
        bookings: []
      },
      {
        id: 2,
        number: "#0002",
        bookings: [
          {
            id: "b1",
            date: weekDates[0],
            timeRange: "9 AM - 10:30 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b2",
            date: weekDates[0],
            timeRange: "12 PM - 1 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b3",
            date: weekDates[0],
            timeRange: "4 PM:6:30 AM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          }
        ]
      },
      {
        id: 3,
        number: "#0003",
        bookings: []
      },
      {
        id: 4,
        number: "#0004",
        bookings: [
          {
            id: "b4",
            date: weekDates[1],
            timeRange: "5 PM - 6 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          }
        ]
      },
      {
        id: 5,
        number: "#0005",
        bookings: [
          {
            id: "b5",
            date: weekDates[1],
            timeRange: "2 PM - 3:30 PM",
            tripName: "Trip name goes...",
            paymentStatus: "outstanding"
          }
        ]
      },
      {
        id: 6,
        number: "#0006",
        bookings: []
      },
      {
        id: 7,
        number: "#0007",
        bookings: [
          {
            id: "b6",
            date: weekDates[3],
            timeRange: "9 AM - 10:30 AM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b7",
            date: weekDates[3],
            timeRange: "12 PM - 1 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b8",
            date: weekDates[3],
            timeRange: "4 PM - 5 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b9",
            date: weekDates[3],
            timeRange: "10 PM - 11 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b10",
            date: weekDates[4],
            timeRange: "5 PM - 6 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b11",
            date: weekDates[6],
            timeRange: "2 PM - 3:30 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b12",
            date: weekDates[6],
            timeRange: "4 PM - 5 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          }
        ]
      },
      {
        id: 8,
        number: "#0008",
        bookings: [
          {
            id: "b13",
            date: weekDates[0],
            timeRange: "4 PM - 4:30 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b14",
            date: weekDates[4],
            timeRange: "2 PM - 3:30 AM",
            tripName: "Trip name goes...",
            paymentStatus: "outstanding"
          }
        ]
      },
      {
        id: 9,
        number: "#0009",
        bookings: [
          {
            id: "b15",
            date: weekDates[2],
            timeRange: "8 AM - 9 AM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          }
        ]
      },
      {
        id: 10,
        number: "#0010",
        bookings: [
          {
            id: "b16",
            date: weekDates[1],
            timeRange: "10 AM - 11 AM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b17",
            date: weekDates[3],
            timeRange: "1 PM - 2 PM",
            tripName: "Trip name goes...",
            paymentStatus: "outstanding"
          }
        ]
      },
      {
        id: 11,
        number: "#0011",
        bookings: []
      },
      {
        id: 12,
        number: "#0012",
        bookings: [
          {
            id: "b18",
            date: weekDates[5],
            timeRange: "6 AM - 7 AM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          }
        ]
      },
      {
        id: 13,
        number: "#0013",
        bookings: [
          {
            id: "b19",
            date: weekDates[0],
            timeRange: "3 PM - 4 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b20",
            date: weekDates[2],
            timeRange: "5 PM - 6 PM",
            tripName: "Trip name goes...",
            paymentStatus: "outstanding"
          }
        ]
      },
      {
        id: 14,
        number: "#0014",
        bookings: []
      },
      {
        id: 15,
        number: "#0015",
        bookings: [
          {
            id: "b21",
            date: weekDates[4],
            timeRange: "9 AM - 10 AM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          }
        ]
      },
      {
        id: 16,
        number: "#0016",
        bookings: [
          {
            id: "b22",
            date: weekDates[1],
            timeRange: "11 AM - 12 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b23",
            date: weekDates[6],
            timeRange: "7 AM - 8 AM",
            tripName: "Trip name goes...",
            paymentStatus: "outstanding"
          }
        ]
      },
      {
        id: 17,
        number: "#0017",
        bookings: []
      },
      {
        id: 18,
        number: "#0018",
        bookings: [
          {
            id: "b24",
            date: weekDates[3],
            timeRange: "2 PM - 3 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          }
        ]
      },
      {
        id: 19,
        number: "#0019",
        bookings: [
          {
            id: "b25",
            date: weekDates[5],
            timeRange: "4 PM - 5 PM",
            tripName: "Trip name goes...",
            paymentStatus: "outstanding"
          }
        ]
      },
      {
        id: 20,
        number: "#0020",
        bookings: [
          {
            id: "b26",
            date: weekDates[0],
            timeRange: "8 AM - 9 AM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          },
          {
            id: "b27",
            date: weekDates[4],
            timeRange: "3 PM - 4 PM",
            tripName: "Trip name goes...",
            paymentStatus: "paid"
          }
        ]
      }
    ]
  }

  // Get assets - will update when selectedDate changes since component re-renders
  const assets = calendarData?.assets || getMockBookings()

  // Total bookings count
  const totalBookingsCount = calendarData?.total_bookings || 7

  // Get bookings for a specific day (used in month view)
  const getBookingsForDay = (date) => {
    const dayBookings = []

    // Filter assets based on selected asset
    const filteredAssets = selectedAsset === "all"
      ? assets
      : assets.filter((asset) => asset.id === Number.parseInt(selectedAsset))

    filteredAssets.forEach((asset) => {
      asset.bookings?.forEach((booking) => {
        const bookingDate = new Date(booking.date)
        if (
          bookingDate.getDate() === date.getDate() &&
          bookingDate.getMonth() === date.getMonth() &&
          bookingDate.getFullYear() === date.getFullYear()
        ) {
          dayBookings.push({
            ...booking,
            assetNumber: asset.number
          })
        }
      })
    })

    return dayBookings
  }

  // Render month view calendar
  const renderMonthView = () => (
    <div className={styles.monthGrid}>
      {/* Header Row with Days of Week */}
      <div className={styles.monthGridHeader}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div key={day} className={styles.monthDayHeader}>
            <Text type="secondary">{day}</Text>
          </div>
        ))}
      </div>

      {/* Calendar Grid Body */}
      <div className={styles.monthGridBody}>
        {getMonthDates().map((dateObj) => {
          const bookings = getBookingsForDay(dateObj.date)
          const hasBookings = bookings.length > 0
          const maxVisible = 3
          const visibleBookings = bookings.slice(0, maxVisible)
          const hiddenCount = bookings.length - maxVisible

          return (
            <div
              key={dateObj.date.toISOString()}
              className={`${styles.monthDayCell} ${!dateObj.isCurrentMonth ? styles.otherMonth : ""}`}
            >
              <div className={styles.monthDayNumber}>
                <Text
                  type={dateObj.isCurrentMonth ? "primary" : "secondary"}
                  className={styles.dayNumberText}
                >
                  {dateObj.dayNumber}
                </Text>
                {hasBookings && (
                  <BookOutlined className={styles.bookingIcon} />
                )}
              </div>

              {/* Bookings List */}
              <div className={styles.monthBookingsList}>
                {visibleBookings.map((booking) => (
                  <div key={booking.id} className={styles.monthBookingItem}>
                    <Text className={styles.monthBookingTime}>
                      {booking.timeRange} <span className={styles.tripText}>{booking.tripName}</span>
                    </Text>
                  </div>
                ))}
                {hiddenCount > 0 && (
                  <div className={styles.viewMoreLink}>
                    <Text type="link" className={styles.viewMoreText}>
                      View more
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )

  // Render week view calendar
  const renderWeekView = () => (
    <div className={styles.weekGrid}>
      {/* Header Row with Days */}
      <div className={styles.weekGridHeader}>
        <div className={styles.assetColumnHeader}>
          <Text type="secondary">{t("equipment.calendar.assets")}</Text>
        </div>
        {getWeekDates().map((date) => {
          const dayName = date.toLocaleDateString("en-US", { weekday: "short" })
          const dayNum = date.getDate()
          return (
            <div key={date.toISOString()} className={styles.dayColumnHeader}>
              <Text type="secondary">{dayName}</Text>
              <Text strong className={styles.dayNumber}>{dayNum}</Text>
            </div>
          )
        })}
      </div>

      {/* Asset Rows */}
      <div className={styles.weekGridBody}>
        {assets.map((asset) => (
          <div key={asset.id} className={styles.assetRow}>
            {/* Asset Name Column */}
            <div className={styles.assetNameCell}>
              <Text>{t("equipment.calendar.asset")} {asset.number}</Text>
            </div>

            {/* Day Columns */}
            {getWeekDates().map((date) => (
              <div key={`${asset.id}-${date.toISOString()}`} className={styles.dayCell}>
                {/* Booking blocks for this asset on this day */}
                {asset.bookings
                  ?.filter((booking) => {
                    const bookingDate = new Date(booking.date)
                    return (
                      bookingDate.getDate() === date.getDate() &&
                      bookingDate.getMonth() === date.getMonth() &&
                      bookingDate.getFullYear() === date.getFullYear()
                    )
                  })
                  .map((booking) => (
                    <div
                      key={booking.id}
                      className={`${styles.weekBookingBlock} ${booking.paymentStatus === "paid" ? styles.paidBooking : styles.outstandingBooking
                        }`}
                    >
                      <Text strong className={styles.bookingTime}>
                        {booking.timeRange}
                      </Text>
                      <Text className={styles.bookingTrip}>{booking.tripName}</Text>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )

  // Render day view calendar
  const renderDayView = () => (
    <div className={styles.calendarGrid}>
      {/* Header Row */}
      <div className={styles.gridHeader}>
        {/* Fixed Time Column Header */}
        <div className={styles.timeColumnHeader}>
          {/* Empty cell for time column */}
        </div>

        {/* Scrollable Asset Headers Container */}
        <div className={styles.assetHeadersContainer}>
          {/* Left Arrow */}
          {showLeftArrow && (
            <Button
              icon={<LeftOutlined />}
              className={styles.scrollArrowLeft}
              onClick={() => handleScroll("left")}
              shape="circle"
            />
          )}

          {/* Scrollable Asset Headers */}
          <div
            className={styles.assetHeadersScroll}
            ref={dayGridRef}
            onScroll={handleHeaderScroll}
          >
            {assets.map((asset) => (
              <div key={asset.id} className={styles.assetColumnHeader}>
                <Text strong>{t("equipment.calendar.asset")} {asset.number}</Text>
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          {showRightArrow && (
            <Button
              icon={<RightOutlined />}
              className={styles.scrollArrowRight}
              onClick={() => handleScroll("right")}
              shape="circle"
            />
          )}
        </div>
      </div>

      {/* Time Slots Grid */}
      <div className={styles.gridBody}>
        {timeSlots.map((timeSlot, timeIndex) => (
          <div key={timeSlot} className={styles.gridRow}>
            {/* Fixed Time Label */}
            <div className={styles.timeLabel}>
              <Text type="secondary">{timeSlot}</Text>
            </div>

            {/* Scrollable Asset Columns Container */}
            <div className={styles.assetColumnsContainer}>
              <div className={styles.assetColumnsScroll}>
                {assets.map((asset) => (
                  <div key={`${timeSlot}-${asset.id}`} className={styles.timeSlotCell}>
                    {/* Booking blocks will be rendered here */}
                    {asset.bookings
                      ?.filter((booking) => booking.startTime === timeIndex)
                      .map((booking) => (
                        <div
                          key={booking.id}
                          className={`${styles.bookingBlock} ${booking.paymentStatus === "paid" ? styles.paidBooking : styles.outstandingBooking
                            }`}
                          style={{ height: `${booking.duration * 60}px` }}
                        >
                          <Text strong className={styles.bookingTime}>
                            {booking.timeRange}
                          </Text>
                          <Text className={styles.bookingTrip}>{booking.tripName}</Text>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  // Render calendar based on view mode
  const renderCalendarGrid = () => {
    if (viewMode === "month") {
      return renderMonthView()
    }

    if (viewMode === "week") {
      return renderWeekView()
    }

    return renderDayView()
  }

  return (
    <Card className={styles.calendarCard}>
      {/* Header */}
      <Flex justify="space-between" align="center" className={styles.header}>
        <Title level={4}>{t("equipment.details.upcoming_bookings")} ({totalBookingsCount})</Title>
        <Button type="primary">{t("equipment.details.add_new_booking")}</Button>
      </Flex>

      {/* Filter Tabs (Day/Week view) or Asset Selector (Month view) */}
      {viewMode === "month" ? (
        <Flex gap={8} className={styles.filterTabs}>
          <Select
            style={{ width: 250 }}
            placeholder={t("equipment.calendar.search_for_asset")}
            value={selectedAsset}
            onChange={setSelectedAsset}
            className={styles.assetSelect}
          >
            <Select.Option value="all">{t("equipment.calendar.all_assets")}</Select.Option>
            {assets.map((asset) => (
              <Select.Option key={asset.id} value={asset.id.toString()}>
                {t("equipment.calendar.asset")} {asset.number}
              </Select.Option>
            ))}
          </Select>
        </Flex>
      ) : (
        <Flex gap={8} className={styles.filterTabs}>
          {filterTabs.map((tab) => (
            <Button
              key={tab.key}
              type={activeFilter === tab.key ? "primary" : "default"}
              onClick={() => setActiveFilter(tab.key)}
              className={activeFilter === tab.key ? styles.activeFilterTab : styles.filterTab}
            >
              {tab.label}
            </Button>
          ))}
        </Flex>
      )}

      {/* Date Navigation and Controls */}
      <Flex justify="space-between" align="center" className={styles.controls}>
        <Flex align="center" gap={16}>
          <Button icon={<LeftOutlined />} onClick={() => navigateDate(-1)} />
          <Text strong className={styles.dateDisplay}>{formatDateDisplay()}</Text>
          <Button icon={<RightOutlined />} onClick={() => navigateDate(1)} />

          {/* View Mode Toggles */}
          <Flex gap={4} className={styles.viewModeToggle}>
            {viewModes.map((mode) => (
              <Button
                key={mode.key}
                type={viewMode === mode.key ? "primary" : "default"}
                onClick={() => handleViewModeChange(mode.key)}
                size="small"
                className={viewMode === mode.key ? styles.activeViewMode : ""}
              >
                {mode.label}
              </Button>
            ))}
          </Flex>
        </Flex>

        {/* Right Controls */}
        <Flex align="center" gap={12}>
          <Button onClick={goToToday}>{t("equipment.calendar.go_to_today")}</Button>
          <Text type="secondary">{t("equipment.calendar.legend")}</Text>
          <Tag color="blue">{t("equipment.calendar.paid")}</Tag>
          <Tag color="orange">{t("equipment.calendar.payment_outstanding")}</Tag>
        </Flex>
      </Flex>

      {/* Journal Section (Day View Only) */}
      {viewMode === "day" && (
        <Flex align="center" className={styles.journalSection}>
          <Text className={styles.journalText}>{formatJournalDate()}</Text>
          <FileTextOutlined className={styles.journalIcon} />
        </Flex>
      )}


      {/* Calendar Grid */}
      {renderCalendarGrid()}
    </Card>
  )
}

EquipmentCalendar.propTypes = {
  calendarData: PropTypes.object,
  loading: PropTypes.bool
}

export default EquipmentCalendar
